import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, Trash2, Calendar, MapPin, User, Mail, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import PartnerCodesSection from "@/components/PartnerCodesSection";
import { CATEGORY_BADGE } from "@/lib/categoryColors";
import { formatDateRangeShort, formatTimeRange } from "@/lib/dateFormat";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_CONFIG = {
  pending: { label: "En attente", className: "bg-[#FEF3C7] text-[#92400E]" },
  validated: { label: "Validé", className: "bg-[#D1FAE5] text-[#065F46]" },
  refused: { label: "Refusé", className: "bg-[#FFE4E6] text-[#9F1239]" },
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("pending");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, validated: 0, refused: 0, partners: 0 });

  const load = async (status) => {
    setLoading(true);
    try {
      const params = status === "all" ? {} : { status };
      const [eventsRes, statsRes] = await Promise.all([
        api.get("/admin/events", { params }),
        api.get("/admin/stats"),
      ]);
      setEvents(eventsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const onValidate = async (id) => {
    try {
      await api.patch(`/admin/events/${id}/validate`);
      toast.success("Événement validé. Notification envoyée au partenaire.");
      load(tab);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onRefuse = async (id) => {
    try {
      await api.patch(`/admin/events/${id}/refuse`);
      toast.success("Événement refusé.");
      load(tab);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Événement supprimé.");
      load(tab);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 fade-in">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-1">Administration</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-ink">Tableau de bord</h1>
          <p className="text-muted_text mt-1">Validez, modifiez ou supprimez les événements soumis.</p>
        </div>
        <Link to="/admin/new">
          <Button className="rounded-xl bg-sage hover:bg-sage-dark text-white h-11 px-5" data-testid="admin-new-event-button">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvel événement
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {[
          { label: "En attente", value: stats.pending, cls: "text-[#92400E]", to: null },
          { label: "Validés", value: stats.validated, cls: "text-[#065F46]", to: null },
          { label: "Refusés", value: stats.refused, cls: "text-[#9F1239]", to: null },
          { label: "Partenaires", value: stats.partners, cls: "text-sage", to: "/admin/partners" },
        ].map((s) => {
          const inner = (
            <>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted_text font-semibold">{s.label}</div>
                {s.to && <span className="text-xs text-sage font-semibold">Voir →</span>}
              </div>
              <div className={`text-2xl sm:text-3xl font-heading font-bold mt-1 ${s.cls}`}>{s.value}</div>
            </>
          );
          const testid = `admin-stat-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`;
          return s.to ? (
            <Link
              key={s.label}
              to={s.to}
              className="bg-white rounded-2xl border border-[#F0E0CC] p-4 sm:p-5 hover:border-sage hover:shadow-md transition-all"
              data-testid={testid}
            >
              {inner}
            </Link>
          ) : (
            <div key={s.label} className="bg-white rounded-2xl border border-[#F0E0CC] p-4 sm:p-5" data-testid={testid}>
              {inner}
            </div>
          );
        })}
      </div>

      <PartnerCodesSection />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#F0E0CC] rounded-xl p-1 h-11">
          <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-sage data-[state=active]:text-white" data-testid="tab-pending">
            En attente {stats.pending > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-white/20 rounded-full">{stats.pending}</span>}
          </TabsTrigger>
          <TabsTrigger value="validated" className="rounded-lg data-[state=active]:bg-sage data-[state=active]:text-white" data-testid="tab-validated">Validés</TabsTrigger>
          <TabsTrigger value="refused" className="rounded-lg data-[state=active]:bg-sage data-[state=active]:text-white" data-testid="tab-refused">Refusés</TabsTrigger>
          <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-sage data-[state=active]:text-white" data-testid="tab-all">Tous</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          <div className="bg-white rounded-2xl border border-[#F0E0CC] overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-muted_text">Chargement…</div>
            ) : events.length === 0 ? (
              <div className="p-10 sm:p-16 text-center" data-testid="empty-admin-events">
                <h3 className="font-heading font-semibold text-lg text-ink">Aucun événement</h3>
                <p className="text-sm text-muted_text mt-1">Aucun événement dans cette catégorie pour le moment.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#F0E0CC]">
                {events.map((e) => {
                  const cfg = STATUS_CONFIG[e.status] || STATUS_CONFIG.pending;
                  return (
                    <li key={e.id} className="p-4 sm:p-5 flex flex-col lg:flex-row gap-4" data-testid="admin-event-row">
                      <img
                        src={e.image_url} alt={e.title}
                        className="w-full lg:w-40 h-40 lg:h-24 object-cover rounded-xl bg-[#F0E0CC]"
                        onError={(ev) => { ev.currentTarget.src = "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=300&q=80"; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 flex-wrap">
                          <h3 className="font-heading font-semibold text-ink">{e.title}</h3>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.className}`}>
                            {cfg.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${CATEGORY_BADGE[e.category] || "bg-[#FEF3C7] text-sage"}`}>{e.category}</span>
                        </div>
                        <p className="text-sm text-muted_text mt-1 line-clamp-2">{e.description}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted_text mt-2">
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDateRangeShort(e.date, e.end_date)} • {formatTimeRange(e.time, e.end_time)}</span>
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.commune}</span>
                          <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {e.creator_name}</span>
                          <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {e.creator_email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap lg:self-center">
                        {e.status !== "validated" && (
                          <Button onClick={() => onValidate(e.id)} size="sm"
                            className="rounded-xl bg-[#065F46] hover:bg-[#054d39] text-white" data-testid="validate-button">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Valider
                          </Button>
                        )}
                        {e.status !== "refused" && (
                          <Button onClick={() => onRefuse(e.id)} size="sm" variant="outline"
                            className="rounded-xl border-[#F0E0CC] text-[#9F1239]" data-testid="refuse-button">
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                          </Button>
                        )}
                        <Link to={`/admin/edit/${e.id}`}>
                          <Button size="sm" variant="outline" className="rounded-xl border-[#F0E0CC]" data-testid="admin-edit-button">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="rounded-xl border-[#F0E0CC] text-red-600" data-testid="admin-delete-button">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cet événement ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible. L'événement "{e.title}" sera définitivement supprimé.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDelete(e.id)} className="bg-red-600 hover:bg-red-700">
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
